import { Suspense, lazy, type ComponentType } from "react";
import { useSeoMetadata } from "@/hooks/useSeoMetadata";
// Hero is eager (above the fold). Deep import bypasses the barrel — going
// through the barrel drags FeaturedEmployers (+ its full pricing/founding-
// partner subtree) and the landing/scarcity API modules onto the FCP critical
// path even though we only need the hero.
// eslint-disable-next-line no-restricted-imports
import { HeroSection } from "@/features/landing/sections/HeroSection";
// TelemetryStrip sits above the hero as a live status bar — import direct so
// it doesn't trigger the barrel either.
// eslint-disable-next-line no-restricted-imports
import { TelemetryStrip } from "@/features/landing/sections/TelemetryStrip";

// Below-the-fold sections — code-split per-section. Import directly from each
// section file, NOT from a barrel, to preserve the per-section chunk boundary.
const EnterpriseLogoCarousel = lazy(() => import("@/features/landing/sections/EnterpriseLogoCarousel").then(m => ({ default: m.EnterpriseLogoCarousel })));
const AudienceSplit = lazy(() => import("@/features/landing/sections/AudienceSplit").then(m => ({ default: m.AudienceSplit })));
const Inventory = lazy(() => import("@/features/landing/sections/Inventory").then(m => ({ default: m.Inventory })));
const EmployerLane = lazy(() => import("@/features/landing/sections/EmployerLane").then(m => ({ default: m.EmployerLane })));
const Comparison = lazy(() => import("@/features/landing/sections/Comparison").then(m => ({ default: m.Comparison })));
const CareerIntelligence = lazy(() => import("@/features/landing/sections/CareerIntelligence").then(m => ({ default: m.CareerIntelligence })));
const CapabilityMatrix = lazy(() => import("@/features/landing/sections/CapabilityMatrix").then(m => ({ default: m.CapabilityMatrix })));
const FinalCta = lazy(() => import("@/features/landing/sections/FinalCta").then(m => ({ default: m.FinalCta })));

const LazySection = ({
  Comp,
  minH,
  label,
}: {
  Comp: ComponentType<unknown>;
  minH: string;
  label: string;
}) => (
  <Suspense fallback={<div aria-hidden aria-label={`Loading ${label}`} className={minH} />}>
    <Comp />
  </Suspense>
);

import { buildFAQPageJsonLd } from "@/lib/seoBuilders";
import { HOMEPAGE_FAQS } from "@/lib/seoFaqs";

/**
 * Landing render order — 11 canonical sections.
 *
 *  01  TelemetryStrip                       (live status bar)
 *  02  HeroSection                          (LCP target, eager)
 *  03  EnterpriseLogoCarousel               (social proof)
 *  04  AudienceSplit + how-it-works         (merged: 3-step flow inside each card)
 *  05  Inventory                            (LiveJobFeed + SpecialtiesGrid, shared chapter)
 *  07  EmployerLane                         (hiring-team pitch, sourced pricing)
 *  08  Comparison                           (vs. generic job boards)
 *  09  CareerIntelligence                   (HeatMap + SignalWire + LearningHub, chapter)
 *  10  CapabilityMatrix                     (feature grid)
 *  11  FinalCta                             (privacy line folded in)
 */
const Index = () => {
  useSeoMetadata({
    title: "Rack & Role | AI, Data Center & Skilled Trades Jobs",
    description:
      "Search jobs by certification, clearance, shift, and systems. Build a profile, follow learning pathways, and grow your data center or trades career.",
    canonicalPath: "/",
    robots: "index, follow",
    jsonLd: buildFAQPageJsonLd(HOMEPAGE_FAQS),
  });

  return (
    <div className="flex flex-1 flex-col overflow-x-clip">
      {/* 01 — Telemetry strip (live status bar, above the hero) */}
      <TelemetryStrip />
      {/* 02 — Hero (eager, LCP) */}
      <HeroSection />
      {/* 03 — Enterprise logo carousel */}
      <LazySection Comp={EnterpriseLogoCarousel} minH="min-h-[320px] md:min-h-[360px]" label="employer carousel" />
      {/* 04 — Audience split with inline how-it-works */}
      <LazySection Comp={AudienceSplit} minH="min-h-[520px] md:min-h-[620px]" label="audience split" />
      {/* 05 — Inventory (live feed + specialties, one chapter) */}
      <LazySection Comp={Inventory} minH="min-h-[820px] md:min-h-[960px]" label="inventory" />
      {/* 07 — Employer lane */}
      <LazySection Comp={EmployerLane} minH="min-h-[520px] md:min-h-[640px]" label="employer lane" />
      {/* 08 — Comparison */}
      <LazySection Comp={Comparison} minH="min-h-[560px] md:min-h-[640px]" label="comparison" />
      {/* 09 — Career intelligence (heat map + signal wire + learning) */}
      <LazySection Comp={CareerIntelligence} minH="min-h-[1400px] md:min-h-[1600px]" label="career intelligence" />
      {/* 10 — Capability matrix */}
      <LazySection Comp={CapabilityMatrix} minH="min-h-[560px] md:min-h-[640px]" label="capabilities" />
      {/* 11 — Final CTA */}
      <LazySection Comp={FinalCta} minH="min-h-[360px] md:min-h-[440px]" label="final cta" />
    </div>
  );
};

export default Index;
